/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { fireEvents } from 'app/shared/lib/fire-events';
import { apiCall } from 'app/shared/lib/api/api';
import { TransportationService } from 'app/um/services/transportation-service';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as UM from 'app/um/models/essette-api';


interface IAuthAppointmentEvent { appointment: UM.AuthTransportationAppointment }
export type AuthAppointmentEvent = CustomEvent<IAuthAppointmentEvent>;

@autoinject()
export class TransportationAppointmentGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public authId!: string;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    protected appointments: ReadonlyArray<UM.AuthTransportationAppointment> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly service: TransportationService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected editAppointment(appointment: UM.AuthTransportationAppointment, copy: boolean = false): void {
        if(appointment && this.$element) {
            const detail: IAuthAppointmentEvent = { appointment: appointment };
            const event = copy ? 'item-copy' : 'item-edit';
            fireEvents(this.$element, event, detail);
        }
    }


    ////****************** Functions ************************************************////
    public clear(): void {
        this.appointments = [];
    }

    public async reload(): Promise<void> {
        this.loadAppointments();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAppointments(): Promise<void> {
        if(!this.authId) return;

        const includes = [
            'dropOff.address',
            'pickUp.address'
        ];

        const fields = [
            'auth.id',
            'comments',
            'dateTime',
            'dropOff.address.attachedTo.type.code',
            'dropOff.address.city',
            'dropOff.address.postalCode',
            'dropOff.address.region.code',
            'dropOff.address.street1',
            'dropOff.address.street2',
            'dropOff.customAddress',
            'dropOff.dateTime',
            'dropOff.isWillCall',
            'endDate',
            'isWillCall',
            'pickup.address.attachedTo.type.code',
            'pickup.address.city',
            'pickup.address.postalCode',
            'pickup.address.region.code',
            'pickup.address.street1',
            'pickup.address.street2',
            'pickUp.customAddress',
            'pickUp.dateTime',
            'pickUp.isWillCall',
            'return.dateTime',
            'return.isWillCall',
            'series'
        ];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAppointmentsByAuth(this.authId, options, includes, fields);
            this.appointments = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deleteAppointment(appointment: UM.AuthTransportationAppointment): Promise<void> {
        const result = await confirmation.showDelete('um:auth.transportationAppointment.$');
        if(result.dialogResponse !== confirmation.options.OK) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.deleteAppointment(appointment.id!);
            isSuccessful = response.isSuccessful;
            await this.reload();
            fireEvents(this.$element, ['item-delete']);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:auth.transportationAppointment.$', isSuccessful);
            this.state.beginSave();
        }
    }
}
